/**
 * Optimizely custom-JS AB test value resolution, shared across verticals.
 * Convention: an Optimizely variation's custom JS sets `window.ABTest<Name>`;
 * we mirror it into localStorage (`abtest.<key>`) so the choice is sticky for
 * the browser and readable synchronously (via getInitialAbTestValue) before
 * the Optimizely snippet has loaded, avoiding a flash on repeat visits.
 */

interface AbTestValueOptions {
  numeric?: boolean
}

function readWindowValue (key: string): unknown {
  return (window as unknown as Record<string, unknown>)[key]
}

/**
 * Synchronous read of a previously-resolved AB test value from localStorage.
 * Use this to initialize refs before the Optimizely snippet has loaded.
 */
export function getInitialAbTestValue (key: string, { numeric = false }: AbTestValueOptions = {}): number | string {
  if (!import.meta.client) return numeric ? 0 : ''

  const stored = getLocal(key)

  if (numeric) {
    const parsed = Number(stored)
    return !isNaN(parsed) ? parsed : 0
  }

  return stored || ''
}

/**
 * Resolves the live AB test value once Optimizely is ready: a stored value is
 * sticky — including `0`, which is a real control assignment, not "unset" —
 * otherwise falls back to `window[windowKey]`. Persists the resolved value.
 */
export function resolveAbTestValue (localStorageKey: string, windowKey: string, { numeric = true }: AbTestValueOptions = {}): number | string {
  if (!import.meta.client) return numeric ? 0 : ''

  const stored = getLocal(localStorageKey)
  const hasStored = stored != null && stored !== '' && (!numeric || !isNaN(+stored))

  let resolved: number | string
  if (hasStored) {
    resolved = numeric ? +stored : (stored as string)
  } else {
    const windowValue = readWindowValue(windowKey)
    resolved = numeric ? (Number(windowValue) || 0) : (typeof windowValue === 'string' ? windowValue : '')
  }

  setLocal(localStorageKey, resolved)

  return resolved
}
